/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useState } from "react";
import ColumnContainer from "./ColumnContainer";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import TaskCard from "./TaskCard";

function KanbanBoard(props: any) {
  const {
    crewData,
    setColumnData,
    setScheduleId,
    setShowScheduled,
    selectedDate,
    crews,
    setLoading,
    loading,
  } = props;
  const [columns, setColumns] = useState<
    { id: string; title: string; crew_id: string }[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [tasks, setTasks] = useState<
    { id: string; columnId: string; title: string }[]
  >([]);
  const [activeTask, setActiveTask] = useState(null);
  const [crewArray, setCrewArray] = useState<any[]>([]);

  useEffect(() => {
    if (crewData) {
      const columnArray: any[] = [];
      const taskArray: any[] = [];

      crewData?.map((crew: any) =>
        columnArray.push({
          ...crew,
          id: crew?.id,
        })
      );

      crewData?.map((crew: any) =>
        crew?.visits?.map((visit: any) =>
          taskArray.push({
            ...visit,
            id: visit?.id,
            columnId: crew?.id,
            title: visit?.property_name,

            crew_id: crew?.id,
          })
        )
      );
      setColumns(columnArray);
      setColumnData(columnArray);
      setTasks(taskArray);
    }
  }, [crewData, setColumns, setTasks, setColumnData]);
  console.log("crewData in kanban:", columns);
  useEffect(() => {
    if (crewData && crews) {
      const dummyCrewArray = crews?.filter((c: any) =>
        crewData?.some((cd: any) => cd.crew_id === c.id)
      );

      setCrewArray(dummyCrewArray);
    }
  }, [crewData, crews]);

  // console.log("columns", columns);
  // console.log(" tasks:", tasks);

  const deleteColumn = (id: string) => {
    const filteredColumns = columns?.filter((e) => e?.id !== id);
    const newTasks = tasks.filter((task) => task.columnId !== id);
    setColumns(filteredColumns);
    setTasks(newTasks);
  };

  const onDragStart = (event: any) => {
    setIsDragging(true);
    if (event.active.data.current?.type === "Task") {
      setActiveTask(event.active.data.current.task);
      return;
    }
  };

  const onDragEnd = (event: any) => {
    setIsDragging(false);
    setActiveTask(null);
    const { over } = event;
    if (!over) return;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  const onDragOver = (event: any) => {
    const { over, active } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === "Task";
    const isOverATask = over.data.current?.type === "Task";

    if (!isActiveATask) return;

    // Helper function to reconstruct crewData and call parent callback
    const updateParentCrewData = (currentTasks: any[]) => {
      const updatedCrewData: any[] = columns.map((col: any) => {
        const tasksForThisColumn = currentTasks.filter(
          (task) => task.columnId === col.id
        );
        const visitsForThisCrew: any[] = tasksForThisColumn.map((task) => ({
          ...task, // Spread the task properties
          id: task.id,
          title: task.title,
          status: task.status,
          startTime: task.startTime,
          endTime: task.endTime,
          address: task.address,
          contract: task.contract,
          origin: task.origin,
        }));

        // Find the original crew object from props.crewData to preserve other properties
        const originalCrew = crewData.find((c: any) => c.id === col.id);

        return {
          ...originalCrew, // Preserve original crew data
          id: col.id,
          name: col.name,
          crew_leader: col.crew_leader,
          hours: col.hours,
          visit: visitsForThisCrew, // Assign the updated visits
        }; // Cast to CrewData, ensuring 'visit' is always defined
      });

      setColumnData(updatedCrewData);
    };

    // Logic for dragging a task over another task (reordering within or moving between columns)
    if (isActiveATask && isOverATask) {
      setTasks((prevTasks) => {
        const activeIndex = prevTasks.findIndex((t) => t.id === activeId);
        const overIndex = prevTasks.findIndex((t) => t.id === overId);

        // Ensure tasks exist at both indices
        if (activeIndex === -1 || overIndex === -1) {
          console.warn("Task not found during drag over, skipping reorder.");
          return prevTasks;
        }

        const newColumnId = prevTasks[overIndex].columnId;

        // Create a new task object with the potentially updated columnId
        const updatedActiveTask = {
          ...prevTasks[activeIndex],
          columnId: newColumnId,
        };

        // Create a temporary array to apply the columnId change before reordering
        const tempTasks = prevTasks.map((task, idx) =>
          idx === activeIndex ? updatedActiveTask : task
        );

        // Apply arrayMove which reorders and returns a new array reference
        const reorderedAndUpdatedTasks = arrayMove(
          tempTasks,
          activeIndex,
          overIndex
        );

        // Update the parent's crewData after the task state is finalized
        updateParentCrewData(reorderedAndUpdatedTasks);

        return reorderedAndUpdatedTasks;
      });
    }

    // Logic for dragging a task over a column (moving to an empty or new column)
    const isOverAColumn = over.data.current?.type === "Column";

    if (isActiveATask && isOverAColumn) {
      setTasks((prevTasks) => {
        const activeIndex = prevTasks.findIndex((t) => t.id === activeId);

        // Ensure task exists
        if (activeIndex === -1) {
          console.warn(
            "Active task not found when dragging over column, skipping move."
          );
          return prevTasks;
        }

        const newColumnId = overId;
        const oldColumnId = prevTasks[activeIndex].columnId;

        // Only update if the columnId has actually changed to prevent unnecessary re-renders/mutations
        if (oldColumnId !== newColumnId) {
          const updatedTasks = prevTasks.map((task, index) => {
            if (index === activeIndex) {
              return { ...task, columnId: newColumnId }; // Update columnId immutably
            }
            return task;
          });
          // When moving to a column, `arrayMove` with same indices effectively
          // ensures a new array is returned and helps with potential re-render issues.
          const finalTasks = arrayMove(updatedTasks, activeIndex, activeIndex);

          // Update the parent's crewData after the task state is finalized
          updateParentCrewData(finalTasks);

          return finalTasks;
        }
        return prevTasks; // No change if already in target column
      });
    }
  };

  console.log("crewArray-----", crewArray);

  return (
    <div className="kanban_board">
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
      >
        <div className="column_row">
          {columns?.map((col, index) => (
            <Fragment key={col.id}>
              <ColumnContainer
                column={col}
                deleteColumn={deleteColumn}
                tasks={tasks.filter((e) => e.columnId === col.id)}
                setScheduleId={setScheduleId}
                setShowScheduled={setShowScheduled}
                crewDrop={columns?.map((c: any) => ({
                  label: (
                    <>
                      <b>{c.crew_name}</b> - {c.crew_leader_name}
                    </>
                  ),
                  value: c.id,
                }))}
                allColumns={columns}
                setColumns={setColumns}
                selectedDate={selectedDate}
                isDragging={isDragging}
                index={index}
                crewArray={crewArray?.find((c: any) => c.id === col.crew_id)}
                setLoading={setLoading}
                loading={loading}
              />
            </Fragment>
          ))}
        </div>

        {createPortal(
          <DragOverlay>
            {activeTask && <TaskCard task={activeTask} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );
}

export default KanbanBoard;
